import {t} from "@lingui/core/macro";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {HeadingLarge} from "@khanacademy/wonder-blocks-typography";
import {
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import Log, {Errors} from "@khan/logging";
import {mediaQueries} from "@khan/shared-styles";

import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import ApolloQuery from "#data-access/gql/apollo-query.tsx";

import {
    CLASS_PICKER_QUERY,
    getGradeLevel,
    mapQueryClassPickerToProps,
    mapQueryToResumeLearningProps,
    shouldSkipGradePicker,
    type ClassPickerQuery,
} from "./courses-data.ts";
import ErrorComponent from "./error.tsx";

const CoursesDashboard = React.lazy(
    () =>
        import(
            "../../content-library-legacy/modules/stateful-courses-dashboard.tsx"
        ),
);
const ResumeLearning = React.lazy(
    () => import("../../content-library-legacy/modules/content-carousel.tsx"),
);

type Props = {
    onSufficientlyUsable: () => void;
} & WithMarkConversionProps;

type State = {
    showError: boolean;
};

class Courses extends React.Component<Props, State> {
    state: State = {
        showError: false,
    };

    componentDidMount() {
        const {markConversion} = this.props;

        markConversion({id: "logged_in_homepage_visit"});
    }

    handleGraphQLError: (error: Error) => void = (error) => {
        Log.error("Failed to query Courses data", Errors.Internal, {
            cause: error,
        });
        this.setState({showError: true});
    };

    render(): React.ReactNode {
        const {onSufficientlyUsable} = this.props;
        const {showError} = this.state;

        return (
            <>
                <ApolloQuery
                    query={CLASS_PICKER_QUERY}
                    fetchPolicy="cache-and-network"
                    onError={this.handleGraphQLError}
                    onCompleted={onSufficientlyUsable}
                >
                    {(query: ClassPickerQuery) => {
                        if (showError) {
                            return <ErrorComponent />;
                        }

                        if (query.loading || !query.data) {
                            return <RouteLoadSpinner />;
                        }

                        const courseDashboardProps =
                            mapQueryClassPickerToProps(query);

                        const resumeLearningProps =
                            mapQueryToResumeLearningProps(query);

                        const classPickerEnabled =
                            query.data &&
                            query.data.classPicker &&
                            query.data.classPicker.enabled;

                        const gradeLevel = getGradeLevel(query);
                        const skipGradePicker = shouldSkipGradePicker(query);

                        if (!classPickerEnabled) {
                            return (
                                <HeadingLarge>
                                    {t`Your courses are not available`}
                                </HeadingLarge>
                            );
                        }

                        return (
                            <View>
                                <React.Suspense fallback={<RouteLoadSpinner />}>
                                    <CoursesDashboard
                                        classPickerData={
                                            courseDashboardProps.classPickerData
                                        }
                                        isFirstTime={
                                            courseDashboardProps.isFirstTime
                                        }
                                        hidePickerIfEmpty={
                                            courseDashboardProps.hidePickerIfEmpty
                                        }
                                        userCourses={
                                            courseDashboardProps.userCourses
                                        }
                                        gradeLevelId={gradeLevel}
                                        skipGradePicker={skipGradePicker}
                                    />
                                </React.Suspense>
                                <React.Suspense fallback={<RouteLoadSpinner />}>
                                    {resumeLearningProps.contentItems.length >
                                        0 && (
                                        <ResumeLearning
                                            {...resumeLearningProps}
                                            hostedInView={true}
                                            minimalHeader={true}
                                            maxItems={3}
                                            breakpoint={
                                                mediaQueries.mdOrSmaller
                                            }
                                        />
                                    )}
                                </React.Suspense>
                            </View>
                        );
                    }}
                </ApolloQuery>
            </>
        );
    }
}

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof Courses,
        React.ComponentProps<typeof Courses>
    >
>;

export default withMarkConversion(Courses) as React.ComponentType<ExportProps>;
